/**
 * IndexedDB layer for offline PWA storage.
 * DB: sentoa-offline
 * Stores: songs | playlists | events
 */

const DB_NAME = 'sentoa-offline';
const DB_VERSION = 1;

export interface OfflineSongMeta {
  id: number;
  assetName: string;
  duration?: number;
}

export interface StoredSong {
  id: number;
  assetName: string;
  duration?: number;
  blob: Blob;
  downloadedAt: number;
}

export interface StoredPlaylist {
  id: number;
  name: string;
  description: string;
  organizationId: number;
  totalDuration: number;
  songs: Array<OfflineSongMeta & { order: number }>;
  downloadedAt: number;
}

export interface StoredEvent {
  id: number;
  name: string;
  description?: string;
  startDateAndTime?: string;
  organizationName?: string;
  status?: string;
  songs: OfflineSongMeta[];
  downloadedAt: number;
}

let _db: IDBDatabase | null = null;

const openDb = (): Promise<IDBDatabase> => {
  if (_db) {
    return Promise.resolve(_db);
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      _db = req.result;
      resolve(req.result);
    };
    req.onupgradeneeded = ({ target }) => {
      const db = (target as IDBOpenDBRequest).result;
      (['songs', 'playlists', 'events'] as const).forEach((name) => {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: 'id' });
        }
      });
    };
  });
};

const run = <T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(store, mode);
        const request = fn(transaction.objectStore(store));

        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () =>
          reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
      })
  );
};

export const offlineDb = {
  // Songs
  getSong: (id: number) => run<StoredSong | undefined>('songs', 'readonly', (s) => s.get(id)),
  getAllSongs: () => run<StoredSong[]>('songs', 'readonly', (s) => s.getAll()),
  putSong: (song: StoredSong) => run<IDBValidKey>('songs', 'readwrite', (s) => s.put(song)),
  deleteSong: (id: number) => run<undefined>('songs', 'readwrite', (s) => s.delete(id)),

  // Playlists
  getPlaylist: (id: number) =>
    run<StoredPlaylist | undefined>('playlists', 'readonly', (s) => s.get(id)),
  getAllPlaylists: () => run<StoredPlaylist[]>('playlists', 'readonly', (s) => s.getAll()),
  putPlaylist: (p: StoredPlaylist) => run<IDBValidKey>('playlists', 'readwrite', (s) => s.put(p)),
  deletePlaylist: (id: number) => run<undefined>('playlists', 'readwrite', (s) => s.delete(id)),

  // Events
  getEvent: (id: number) => run<StoredEvent | undefined>('events', 'readonly', (s) => s.get(id)),
  getAllEvents: () => run<StoredEvent[]>('events', 'readonly', (s) => s.getAll()),
  putEvent: (e: StoredEvent) => run<IDBValidKey>('events', 'readwrite', (s) => s.put(e)),
  deleteEvent: (id: number) => run<undefined>('events', 'readwrite', (s) => s.delete(id))
};
